/**
 * ============================================================================
 * SupplierTimeline — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: vertical timeline of supplier lifecycle events. Follows the
 *          DocumentDetail (Phase 5.5) pattern — a connecting rail on the
 *          inline-start edge, one card per event with a typed dot anchor.
 *
 * A11Y: rendered as an <ol> so the order is announced; each event card
 *  exposes the kind via aria-label and via a visible mono-font tag.
 * ============================================================================ */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SupplierDetail.module.css';
import type {
  SupplierEventKind,
  SupplierTimelineEvent,
} from './useSupplierDetailQuery';

const KIND_DOT: Record<SupplierEventKind, string> = {
  CREATED: styles.dotInfo,
  QUESTIONNAIRE_ISSUED: styles.dotInfo,
  QUESTIONNAIRE_RECEIVED: styles.dotInfo,
  AUDIT_SCHEDULED: styles.dotWarn,
  AUDIT_COMPLETED: styles.dotInfo,
  RISK_ASSESSED: styles.dotWarn,
  EVALUATION_SIGNED: styles.dotNeutral,
  LIFECYCLE_TRANSITION: styles.dotInfo,
  DOCUMENT_LINKED: styles.dotNeutral,
  CAPA_OPENED: styles.dotCritical,
  CAPA_CLOSED: styles.dotNeutral,
  DISQUALIFIED: styles.dotCritical,
  OTHER: styles.dotNeutral,
};

export interface SupplierTimelineProps {
  events: SupplierTimelineEvent[];
}

export function SupplierTimeline({ events }: SupplierTimelineProps) {
  const { t, i18n } = useTranslation();
  const dateFmt = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [i18n.language],
  );

  if (events.length === 0) {
    return (
      <p className={styles.timelineEmpty}>
        {t('supplier.detail.timeline.empty', {
          defaultValue: 'No lifecycle events recorded yet.',
        })}
      </p>
    );
  }

  return (
    <ol
      className={styles.timeline}
      aria-label={t('supplier.detail.timeline.listAria', {
        defaultValue: 'Supplier lifecycle timeline',
      })}
    >
      {events.map((ev) => {
        const kindLabel = t(`supplier.detail.timeline.kind.${ev.kind}`, {
          defaultValue: ev.kind,
        });
        return (
          <li key={ev.event_id} className={styles.timelineItem}>
            <div
              className={`${styles.timelineDot} ${KIND_DOT[ev.kind]}`}
              aria-hidden="true"
            />
            <article
              className={styles.timelineCard}
              aria-label={t('supplier.detail.timeline.itemAria', {
                defaultValue: '{{kind}} on {{ts}}',
                kind: kindLabel,
                ts: dateFmt.format(new Date(ev.ts_utc)),
              })}
            >
              <header className={styles.timelineHeader}>
                <span className={styles.timelineKind}>{kindLabel}</span>
                <time
                  className={styles.timelineTs}
                  dateTime={ev.ts_utc}
                >
                  {dateFmt.format(new Date(ev.ts_utc))}
                </time>
              </header>
              <h3 className={styles.timelineTitle}>{ev.title}</h3>
              {ev.description && (
                <p className={styles.timelineDesc}>{ev.description}</p>
              )}
              {ev.actor && (
                <p className={styles.timelineActor}>
                  {t('supplier.detail.timeline.actor', {
                    defaultValue: 'By {{actor}}',
                    actor: ev.actor,
                  })}
                </p>
              )}
            </article>
          </li>
        );
      })}
    </ol>
  );
}
